import { Heading, Paragraph, Switch, showToast, cn } from '@quickmd-llc/design-system'
import { getErrorObject } from '@quickmd-llc/frontend-utils'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetAdminsUserIdPermissionsQueryKey,
  useGetAdminsPermissions,
  useGetAdminsUserIdPermissions,
  usePutAdminsUserIdPermissions,
} from '@/api/generated/admin/admin'
import { AdminPermission, type AdminPermissionsApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { getPermissionLabel } from './permissionLabels'

type Props = {
  userId: string
}

const GRID_COLUMNS = 'minmax(0, 1fr) auto'

/**
 * Permissions that must never be shown or toggled in the UI. `admin.permissions.manage`
 * gates who can edit permissions at all — it can only be changed directly in the database.
 * It is still preserved in the PUT payload (computed from the full granted set) so toggling
 * other permissions never strips it.
 */
const HIDDEN_PERMISSIONS = new Set<AdminPermission>([AdminPermission.adminpermissionsmanage])

export function PermissionsSection({ userId }: Props) {
  const queryClient = useQueryClient()
  const grantedQueryKey = getGetAdminsUserIdPermissionsQueryKey(userId)

  // All permissions available in the system.
  const {
    data: availableData,
    isLoading: isAvailableLoading,
    isError: isAvailableError,
    error: availableError,
  } = useGetAdminsPermissions()

  // Permissions granted to the admin being viewed.
  const {
    data: grantedData,
    isLoading: isGrantedLoading,
    isError: isGrantedError,
    error: grantedError,
  } = useGetAdminsUserIdPermissions(userId, { query: { enabled: !!userId } })

  const { mutate: updatePermissions, isPending } = usePutAdminsUserIdPermissions()

  const isLoading = isAvailableLoading || isGrantedLoading
  const isError = isAvailableError || isGrantedError
  const error = availableError ?? grantedError

  const availablePermissions = [...(availableData?.Permissions ?? [])]
    .filter((permission) => !HIDDEN_PERMISSIONS.has(permission))
    .sort((a, b) => getPermissionLabel(a).localeCompare(getPermissionLabel(b)))
  const grantedPermissions = new Set(grantedData?.Permissions ?? [])

  const handleToggle = (permission: AdminPermission, nextChecked: boolean) => {
    // Recompute from the freshest cache (not a stale render closure) so rapid
    // cross-row toggles never drop a permission the previous toggle just set.
    const previous = queryClient.getQueryData<AdminPermissionsApiOutContract>(grantedQueryKey)
    const next = new Set(previous?.Permissions ?? grantedData?.Permissions ?? [])
    if (nextChecked) {
      next.add(permission)
    } else {
      next.delete(permission)
    }
    const nextPermissions = Array.from(next)

    // Optimistic update — reflect the toggle immediately; roll back if the PUT fails.
    queryClient.setQueryData<AdminPermissionsApiOutContract>(grantedQueryKey, { Permissions: nextPermissions })

    updatePermissions(
      { userId, data: { Permissions: nextPermissions } },
      {
        onSuccess: () => {
          showToast({
            type: 'success',
            message: `${getPermissionLabel(permission)} ${nextChecked ? 'granted' : 'revoked'}`,
            position: 'bottom-right',
          })
        },
        onError: (err) => {
          queryClient.setQueryData(grantedQueryKey, previous)
          showToast({
            type: 'error',
            message: getErrorObject(err, 'Failed to update permission.').message,
            position: 'bottom-right',
          })
        },
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: grantedQueryKey })
        },
      },
    )
  }

  return (
    <div className='w-full'>
      <Heading level='h3'>Permissions</Heading>

      <div className='mt-sp-2 rounded-lg border border-border-dark bg-white'>
        {isLoading ?
          <Paragraph size='md' className='px-sp-4 py-sp-3 text-text-medium'>
            Loading permissions…
          </Paragraph>
        : null}

        {!isLoading && isError ?
          <Paragraph size='md' className='px-sp-4 py-sp-3' error>
            {getErrorObject(error, 'Failed to load permissions.').message}
          </Paragraph>
        : null}

        {!isLoading && !isError && availablePermissions.length === 0 && (
          <Paragraph size='md' className='px-sp-4 py-sp-3 text-text-medium'>
            No permissions available.
          </Paragraph>
        )}

        {!isLoading &&
          !isError &&
          availablePermissions.map((permission, index) => {
            const isGranted = grantedPermissions.has(permission)
            const isLast = index === availablePermissions.length - 1
            return (
              <div
                key={permission}
                className={cn('grid items-center gap-sp-3 px-sp-4 py-sp-3', !isLast && 'border-b border-shade-10')}
                style={{ gridTemplateColumns: GRID_COLUMNS }}>
                <Paragraph size='md' className='font-semibold text-text-dark'>
                  {getPermissionLabel(permission)}
                </Paragraph>
                <Switch
                  size='s'
                  checked={isGranted}
                  disabled={isPending}
                  onChange={(nextChecked: boolean) => handleToggle(permission, nextChecked)}
                  aria-label={getPermissionLabel(permission)}
                />
              </div>
            )
          })}
      </div>
    </div>
  )
}
